/**
 * The paste importer of a lesson's word list: text → rows (with per-line errors), and rows →
 * the body of `POST /vocabulary/lessons/:lessonId` for either import mode.
 *
 * One line = one word: `russian, uzbek[, english[, russian example]]`. A line uses ONE
 * separator, the first of these it contains: a tab, a semicolon, a spaced dash (` — `, ` – `
 * or ` - `), a comma. So `кто-то` (no spaces) stays one word, and an example sentence in the
 * last column may keep its own commas: everything after the third separator is the example.
 */

/** Column limits of the API's VocabularyItemDto. */
export const WORD_LIMITS = { term: 120, translationUz: 200, translationEn: 200, exampleRu: 400 };

const SEPARATORS = [
  // One tab per column: a spreadsheet's empty cell (two tabs in a row) stays an empty column.
  { test: /\t/, split: /\t/, join: '\t' },
  { test: /;/, split: /\s*;\s*/, join: '; ' },
  { test: /\s[—–]\s/, split: /\s+[—–]\s+/, join: ' — ' },
  { test: /\s-\s/, split: /\s+-\s+/, join: ' - ' },
  { test: /,/, split: /\s*,\s*/, join: ', ' },
];

/** "1. дом - uy" and "3) дом - uy": a numbered list's numbers are not part of the word. */
const LIST_NUMBER = /^\s*\d{1,3}[.)]\s+/;

/** The API matches a pasted word to a stored one on the trimmed, lower-cased term. */
export const termKey = (term) => String(term || '').trim().toLowerCase();

function splitLine(line) {
  const sep = SEPARATORS.find((s) => s.test.test(line));
  if (!sep) return [line.trim()];
  const parts = line.split(sep.split).map((c) => c.trim());
  if (parts.length <= 4) return parts;
  return [...parts.slice(0, 3), parts.slice(3).join(sep.join)];
}

/**
 * @param {string} text
 * @returns {{ rows: Array<{ line: number, term: string, translationUz: string, translationEn: string, exampleRu: string }>,
 *            errors: Array<{ line: number, text: string, reason: 'missingTranslation'|'tooLong'|'duplicate', column?: string, firstLine?: number }> }}
 *   `line` is 1-based. A line with an error is not in `rows`; `column` names the field over
 *   its limit, `firstLine` the earlier line holding the same word.
 */
export function parseWordList(text) {
  const rows = [];
  const errors = [];
  const seen = new Map();

  String(text || '').split(/\r?\n/).forEach((raw, index) => {
    const line = index + 1;
    const content = raw.replace(LIST_NUMBER, '').trim();
    if (!content) return;

    const [term = '', translationUz = '', translationEn = '', exampleRu = ''] = splitLine(content);
    if (!term || !translationUz) {
      errors.push({ line, text: raw.trim(), reason: 'missingTranslation' });
      return;
    }
    const row = { line, term, translationUz, translationEn, exampleRu };
    const tooLong = Object.keys(WORD_LIMITS).find((key) => row[key].length > WORD_LIMITS[key]);
    if (tooLong) {
      errors.push({ line, text: raw.trim(), reason: 'tooLong', column: tooLong });
      return;
    }
    const key = termKey(term);
    if (seen.has(key)) {
      errors.push({ line, text: raw.trim(), reason: 'duplicate', firstLine: seen.get(key) });
      return;
    }
    seen.set(key, line);
    rows.push(row);
  });

  return { rows, errors };
}

/**
 * Rows → the replace body. Both modes send only the columns the paste filled (plus the
 * English a stored word already has, so a two-column paste does not blank it): the API
 * keeps every column a body leaves out (pictures, audio, stress, grammar, examples).
 *
 * - `append` (default): the stored words stay first, in their order; a pasted word the lesson
 *   already has updates that word in place; new words go after them.
 * - `replace`: the lesson's list becomes exactly the pasted words; stored words not pasted
 *   are removed.
 *
 * @param {object[]} existingWords the lesson's current words (from GET /vocabulary/lessons/:id)
 * @param {object[]} rows parseWordList(...).rows
 * @param {'append'|'replace'} mode
 * @returns {{ items: object[], added: number, updated: number, kept: number, removed: number }}
 */
export function buildImportItems(existingWords, rows, mode = 'append') {
  const stored = new Map((existingWords || []).map((w) => [termKey(w.term), w]));
  const pasted = (row) => {
    const match = stored.get(termKey(row.term));
    return {
      ...(match ? { id: match.id } : {}),
      term: row.term,
      translationUz: row.translationUz,
      translationEn: row.translationEn || match?.translationEn || '',
      ...(row.exampleRu ? { exampleRu: row.exampleRu } : {}),
    };
  };

  if (mode === 'replace') {
    const keys = new Set(rows.map((r) => termKey(r.term)));
    const updated = rows.filter((r) => stored.has(termKey(r.term))).length;
    return {
      items: rows.map((row, orderIndex) => ({ ...pasted(row), orderIndex })),
      added: rows.length - updated,
      updated,
      kept: 0,
      removed: (existingWords || []).filter((w) => !keys.has(termKey(w.term))).length,
    };
  }

  const byKey = new Map(rows.map((r) => [termKey(r.term), r]));
  const items = (existingWords || []).map((word) => {
    const row = byKey.get(termKey(word.term));
    if (row) return pasted(row);
    return {
      id: word.id,
      term: word.term,
      translationUz: word.translationUz || '',
      translationEn: word.translationEn || '',
    };
  });
  const fresh = rows.filter((r) => !stored.has(termKey(r.term)));
  fresh.forEach((row) => items.push(pasted(row)));

  const updated = rows.length - fresh.length;
  return {
    items: items.map((item, orderIndex) => ({ ...item, orderIndex })),
    added: fresh.length,
    updated,
    kept: (existingWords || []).length - updated,
    removed: 0,
  };
}
